// Question generators. Every builder returns an array of quiz items:
// { ask, voice?, correctKey, prompt: {type,...}, options: [{key,label,emoji?,img?,color?,shape?,group?}], cols?, hideLabels? }
import { LETTERS, COUNT_EMOJIS, COLORS, SHAPES, ANIMALS, HABITATS, FRUITS, UNHEALTHY, EVERYDAY, EVERYDAY_ITEMS } from "./data";

export const shuffle = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };
export const pick = (arr, n) => shuffle(arr).slice(0, n);
const others = (arr, item, n, keyOf = (x) => x.key) => pick(arr.filter(x => keyOf(x) !== keyOf(item)), n);
const withCorrect = (correct, distractors) => shuffle([correct, ...distractors]);
const rint = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));

/* ---------- ABC ---------- */
const letterOpt = (l, lower = false) => ({ key: l.key, label: lower ? l.letter.toLowerCase() : l.letter, big: lower ? l.letter.toLowerCase() : l.letter });
const wordOpt = (l) => ({ key: l.key, label: l.word, emoji: l.emoji });

export const abcModes = {
  recognition: () => pick(LETTERS, 8).map(l => ({
    ask: `Which letter is "${l.letter}"?`, voice: `Find the letter ${l.letter}`, correctKey: l.key,
    prompt: { type: "speaker", text: l.letter }, options: withCorrect(letterOpt(l), others(LETTERS, l, 3).map(x => letterOpt(x))),
  })),
  sounds: () => LETTERS.map(l => ({
    ask: `${l.letter} is for...`, voice: `${l.letter}. ${l.letter} is for ${l.word}`, correctKey: l.key,
    prompt: { type: "letter", text: l.letter }, options: withCorrect(wordOpt(l), others(LETTERS, l, 2).map(wordOpt)),
  })),
  find: () => pick(LETTERS, 8).map(l => ({
    ask: `Find the letter ${l.letter}`, voice: `Find the letter ${l.letter}`, correctKey: l.key,
    prompt: { type: "none" }, cols: 3, options: withCorrect(letterOpt(l), others(LETTERS, l, 5).map(x => letterOpt(x))),
  })),
  match: () => pick(LETTERS, 8).map(l => ({
    ask: `Which picture starts with "${l.letter}"?`, voice: `What starts with ${l.letter}?`, correctKey: l.key,
    prompt: { type: "letter", text: l.letter }, options: withCorrect(wordOpt(l), others(LETTERS, l, 3).map(wordOpt)),
  })),
  case: () => pick(LETTERS, 8).map(l => ({
    ask: `Find the small letter for "${l.letter}"`, voice: `Find the lowercase ${l.letter}`, correctKey: l.key,
    prompt: { type: "letter", text: l.letter }, options: withCorrect(letterOpt(l, true), others(LETTERS, l, 3).map(x => letterOpt(x, true))),
  })),
};

/* ---------- Numbers ---------- */
const numOpt = (n) => ({ key: String(n), label: String(n), big: String(n) });
const numDistractors = (n, k, max = 20) => {
  const near = Array.from({ length: max }, (_, i) => i + 1).filter(d => d !== n).sort((a, b) => Math.abs(a - n) - Math.abs(b - n)).slice(0, k + 3);
  return shuffle(near).slice(0, Math.min(k, near.length));
};

export const numberModes = {
  recognition: () => Array.from({ length: 20 }, (_, i) => i + 1).map(n => ({
    ask: `Which number is ${n}?`, voice: `Find the number ${n}`, correctKey: String(n),
    prompt: { type: "speaker", text: String(n) }, options: withCorrect(numOpt(n), numDistractors(n, 3).map(numOpt)),
  })),
  count: () => pick(Array.from({ length: 10 }, (_, i) => i + 1), 8).map(n => {
    const e = COUNT_EMOJIS[rint(0, COUNT_EMOJIS.length - 1)];
    return { ask: "How many?", voice: "How many can you count?", correctKey: String(n), prompt: { type: "group", emoji: e, n }, options: withCorrect(numOpt(n), numDistractors(n, 2, 10).map(numOpt)) };
  }),
  moreless: () => Array.from({ length: 8 }, (_, i) => {
    const a = rint(1, 9); let b = rint(1, 9); while (b === a) b = rint(1, 9);
    const more = i % 2 === 0; const e1 = COUNT_EMOJIS[rint(0, 4)], e2 = COUNT_EMOJIS[rint(5, 9)];
    const correct = more ? (a > b ? "a" : "b") : (a < b ? "a" : "b");
    return { ask: more ? "Which group has MORE?" : "Which group has LESS?", voice: more ? "Which has more?" : "Which has less?", correctKey: correct,
      prompt: { type: "none" }, options: [{ key: "a", label: `${a}`, group: { emoji: e1, n: a } }, { key: "b", label: `${b}`, group: { emoji: e2, n: b } }] };
  }),
};
export const numberOrderRounds = () => Array.from({ length: 5 }, () => { const start = rint(1, 16); return [start, start + 1, start + 2, start + 3, start + 4].slice(0, rint(3, 5)); });

/* ---------- Colors ---------- */
const colorOpt = (c) => ({ key: c.key, label: c.label, color: c.hex });
const objOpt = (o, key) => ({ key, label: o[0], emoji: o[1] });

export const colorModes = {
  identify: () => pick(COLORS, 8).map(c => ({
    ask: "What color is this?", voice: "What color is this?", correctKey: c.key,
    prompt: { type: "color", hex: c.hex }, options: withCorrect({ key: c.key, label: c.label, text: c.label }, others(COLORS, c, 2).map(x => ({ key: x.key, label: x.label, text: x.label }))),
  })),
  objects: () => pick(COLORS.filter(c => c.objects.length >= 2), 8).map(c => {
    const o = c.objects[rint(0, c.objects.length - 1)];
    const dis = others(COLORS, c, 3).map(x => objOpt(x.objects[0], x.key));
    return { ask: `Which one is ${c.label.toUpperCase()}?`, voice: `Which one is ${c.label}?`, correctKey: c.key, prompt: { type: "color", hex: c.hex, label: c.label }, options: withCorrect(objOpt(o, c.key), dis) };
  }),
  find: () => pick(COLORS, 8).map(c => ({
    ask: `Find ${c.label.toUpperCase()}`, voice: `Find the color ${c.label}`, correctKey: c.key,
    prompt: { type: "none" }, cols: 3, options: withCorrect(colorOpt(c), others(COLORS, c, 5).map(colorOpt)),
  })),
};
export const colorSortRounds = () => Array.from({ length: 3 }, () => {
  const bins = pick(COLORS.filter(c => c.objects.length >= 3), 2);
  const items = bins.flatMap(b => pick(b.objects, 3).map(o => ({ key: `${b.key}-${o[0]}`, label: o[0], emoji: o[1], bin: b.key })));
  return { title: "Sort by color", bins: bins.map(b => ({ key: b.key, label: b.label, color: b.hex })), items: shuffle(items) };
});

/* ---------- Shapes ---------- */
const shapeOpt = (s) => ({ key: s.key, label: s.label, shape: s.key });
export const shapeModes = {
  recognition: () => pick(SHAPES, 8).map(s => ({
    ask: "What shape is this?", voice: "What shape is this?", correctKey: s.key,
    prompt: { type: "shape", shape: s.key }, options: withCorrect({ key: s.key, label: s.label, text: s.label }, others(SHAPES, s, 2).map(x => ({ key: x.key, label: x.label, text: x.label }))),
  })),
  match: () => pick(SHAPES, 8).map(s => ({
    ask: "Find the same shape", voice: "Find the matching shape", correctKey: s.key,
    prompt: { type: "shape", shape: s.key }, options: withCorrect(shapeOpt(s), others(SHAPES, s, 3).map(shapeOpt)),
  })),
  find: () => pick(SHAPES, 8).map(s => ({
    ask: `Find the ${s.label.toUpperCase()}`, voice: `Find the ${s.label}`, correctKey: s.key,
    prompt: { type: "none" }, cols: 3, options: withCorrect(shapeOpt(s), others(SHAPES, s, 5).map(shapeOpt)),
  })),
  objects: () => pick(SHAPES.slice(0, 8), 8).map(s => {
    const o = s.objects[rint(0, s.objects.length - 1)];
    return { ask: `Which looks like a ${s.label.toLowerCase()}?`, voice: `Which object looks like a ${s.label}?`, correctKey: s.key,
      prompt: { type: "shape", shape: s.key }, options: withCorrect(objOpt(o, s.key), others(SHAPES.slice(0, 8), s, 3).map(x => objOpt(x.objects[0], x.key))) };
  }),
};
export const shapeSortRounds = () => Array.from({ length: 3 }, () => {
  const bins = pick(SHAPES.slice(0, 7), 2);
  const items = bins.flatMap(b => pick(b.objects, Math.min(3, b.objects.length)).map(o => ({ key: `${b.key}-${o[0]}`, label: o[0], emoji: o[1], bin: b.key })));
  return { title: "Sort by shape", bins: bins.map(b => ({ key: b.key, label: b.label, shape: b.key })), items: shuffle(items) };
});

/* ---------- Animals ---------- */
const animalOpt = (a) => ({ key: a.key, label: a.label, emoji: a.emoji, img: a.img });
export const animalModes = {
  recognition: () => pick(ANIMALS, 8).map(a => ({
    ask: `Which one is the ${a.label}?`, voice: `Find the ${a.label}`, correctKey: a.key,
    prompt: { type: "speaker", text: a.label }, options: withCorrect(animalOpt(a), others(ANIMALS, a, 3).map(animalOpt)),
  })),
  sounds: () => pick(ANIMALS, 8).map(a => ({
    ask: `Which animal says "${a.sound}"?`, voice: `Which animal says ${a.sound}?`, correctKey: a.key,
    prompt: { type: "speaker", text: a.sound }, options: withCorrect(animalOpt(a), others(ANIMALS, a, 3).map(animalOpt)),
  })),
  soundmatch: () => pick(ANIMALS, 8).map(a => ({
    ask: `What sound does the ${a.label} make?`, voice: `What does the ${a.label} say?`, correctKey: a.key,
    prompt: { type: "image", img: a.img, emoji: a.emoji }, options: withCorrect({ key: a.key, label: a.sound, text: a.sound }, others(ANIMALS, a, 2).map(x => ({ key: x.key, label: x.sound, text: x.sound }))),
  })),
  habitat: () => pick(ANIMALS, 8).map(a => {
    const h = HABITATS.find(x => x.key === a.habitat);
    return { ask: `Where does the ${a.label} live?`, voice: `Where does the ${a.label} live?`, correctKey: h.key,
      prompt: { type: "image", img: a.img, emoji: a.emoji }, options: withCorrect({ key: h.key, label: h.label, emoji: h.emoji }, others(HABITATS, h, 2).map(x => ({ key: x.key, label: x.label, emoji: x.emoji }))) };
  }),
  find: () => pick(ANIMALS, 8).map(a => ({
    ask: `Find the ${a.label}`, voice: `Find the ${a.label}`, correctKey: a.key,
    prompt: { type: "none" }, cols: 3, options: withCorrect(animalOpt(a), others(ANIMALS, a, 5).map(animalOpt)),
  })),
};

/* ---------- Fruits ---------- */
const fruitOpt = (f) => ({ key: f.key, label: f.label, emoji: f.emoji });
export const fruitModes = {
  recognition: () => pick(FRUITS, 8).map(f => ({
    ask: `Which one is the ${f.label}?`, voice: `Find the ${f.label}`, correctKey: f.key,
    prompt: { type: "speaker", text: f.label }, options: withCorrect(fruitOpt(f), others(FRUITS, f, 3).map(fruitOpt)),
  })),
  name: () => pick(FRUITS, 8).map(f => ({
    ask: "What is this fruit called?", voice: "What is this fruit called?", correctKey: f.key,
    prompt: { type: "emoji", emoji: f.emoji }, options: withCorrect({ key: f.key, label: f.label, text: f.label }, others(FRUITS, f, 2).map(x => ({ key: x.key, label: x.label, text: x.label }))),
  })),
  colors: () => pick(FRUITS, 8).map(f => {
    const c = COLORS.find(x => x.key === f.color);
    return { ask: `What color is the ${f.label}?`, voice: `What color is the ${f.label}?`, correctKey: c.key,
      prompt: { type: "emoji", emoji: f.emoji }, options: withCorrect(colorOpt(c), others(COLORS.slice(0, 8), c, 2).map(colorOpt)) };
  }),
  healthy: () => Array.from({ length: 8 }, () => {
    const f = FRUITS[rint(0, FRUITS.length - 1)];
    return { ask: "Which is the healthy snack?", voice: "Which one is healthy?", correctKey: f.key, prompt: { type: "none" }, options: withCorrect(fruitOpt(f), pick(UNHEALTHY, 2)) };
  }),
  count: () => pick(Array.from({ length: 10 }, (_, i) => i + 1), 8).map(n => {
    const f = FRUITS[rint(0, FRUITS.length - 1)];
    return { ask: `How many ${f.label.toLowerCase()}s?`, voice: `How many ${f.label}s?`, correctKey: String(n), prompt: { type: "group", emoji: f.emoji, n }, options: withCorrect(numOpt(n), numDistractors(n, 2, 10).map(numOpt)) };
  }),
};

/* ---------- Everyday ---------- */
const itemOpt = (i) => ({ key: i.key, label: i.label, emoji: i.emoji });
const groupMode = (groupKey) => () => {
  const g = EVERYDAY.find(x => x.key === groupKey);
  return pick(g.items, 8).map(it => ({
    ask: `Find the ${it.label}`, voice: `Find the ${it.label}`, correctKey: it.key,
    prompt: { type: "speaker", text: it.label }, options: withCorrect(itemOpt(it), others(g.items, it, 3).map(itemOpt)),
  }));
};
export const everydayModes = {
  toys: groupMode("toys"), clothes: groupMode("clothes"), food: groupMode("food"), household: groupMode("household"), school: groupMode("school"),
  identify: () => pick(EVERYDAY_ITEMS, 8).map(it => {
    const g = EVERYDAY.find(x => x.key === it.group);
    return { ask: `The ${it.label} is a...`, voice: `What kind of thing is a ${it.label}?`, correctKey: g.key,
      prompt: { type: "emoji", emoji: it.emoji }, options: withCorrect({ key: g.key, label: g.label, emoji: g.emoji }, others(EVERYDAY, g, 2).map(x => ({ key: x.key, label: x.label, emoji: x.emoji }))) };
  }),
};

/* ---------- Counting section ---------- */
const countWith = (pool, labelOf) => () => pick(Array.from({ length: 10 }, (_, i) => i + 1), 8).map(n => {
  const it = pool[rint(0, pool.length - 1)];
  return { ask: `How many ${labelOf(it)}?`, voice: `How many ${labelOf(it)}?`, correctKey: String(n), prompt: { type: "group", emoji: it.emoji, n }, options: withCorrect(numOpt(n), numDistractors(n, 2, 10).map(numOpt)) };
});
export const countingModes = {
  objects: () => pick(Array.from({ length: 20 }, (_, i) => i + 1), 8).map(n => {
    const e = COUNT_EMOJIS[rint(0, COUNT_EMOJIS.length - 1)];
    return { ask: "Count them all!", voice: "How many are there?", correctKey: String(n), prompt: { type: "group", emoji: e, n, small: n > 10 }, options: withCorrect(numOpt(n), numDistractors(n, 2).map(numOpt)) };
  }),
  select: () => pick(Array.from({ length: 20 }, (_, i) => i + 1), 8).map(n => {
    const e = COUNT_EMOJIS[rint(0, COUNT_EMOJIS.length - 1)];
    return { ask: "Tap the correct number", voice: "Count and tap the number", correctKey: String(n), prompt: { type: "group", emoji: e, n, small: n > 10 }, cols: 3, options: withCorrect(numOpt(n), numDistractors(n, 5).map(numOpt)) };
  }),
  animals: countWith(ANIMALS, a => a.label.toLowerCase() + "s"),
  fruits: countWith(FRUITS, f => f.label.toLowerCase() + "s"),
  toys: countWith(EVERYDAY[0].items, t => t.label.toLowerCase() + "s"),
  addition: () => Array.from({ length: 8 }, () => {
    const a = rint(1, 5), b = rint(1, 5), e = COUNT_EMOJIS[rint(0, COUNT_EMOJIS.length - 1)];
    return { ask: `${a} + ${b} = ?`, voice: `${a} plus ${b} equals?`, correctKey: String(a + b), prompt: { type: "add", emoji: e, a, b }, options: withCorrect(numOpt(a + b), numDistractors(a + b, 2, 10).map(numOpt)) };
  }),
};

/* ---------- Matching section (pairs) ---------- */
const pairs = (n, list, left, right) => pick(list, n).map(x => ({ id: x.key, left: left(x), right: right(x) }));
export const matchingModes = {
  picture: () => Array.from({ length: 3 }, () => pairs(4, ANIMALS, a => ({ emoji: a.emoji }), a => ({ emoji: a.emoji }))),
  name: () => Array.from({ length: 3 }, () => pairs(4, FRUITS, f => ({ emoji: f.emoji }), f => ({ text: f.label }))),
  letter: () => Array.from({ length: 3 }, () => pairs(4, LETTERS, l => ({ big: l.letter }), l => ({ emoji: l.emoji }))),
  quantity: () => Array.from({ length: 3 }, () => pick([1, 2, 3, 4, 5, 6], 4).map(n => ({ id: String(n), left: { big: String(n) }, right: { group: { emoji: "⭐", n } } }))),
  color: () => Array.from({ length: 3 }, () => pairs(4, COLORS.slice(0, 8), c => ({ color: c.hex }), c => ({ emoji: c.objects[0][1] }))),
  shape: () => Array.from({ length: 3 }, () => pairs(4, SHAPES.slice(0, 8), s => ({ shape: s.key }), s => ({ emoji: s.objects[0][1] }))),
};

/* ---------- Sort & Match game ---------- */
export const sortMatchRounds = () => shuffle([
  ...colorSortRounds().slice(0, 1), ...shapeSortRounds().slice(0, 1),
  (() => { const g = pick(EVERYDAY, 2); return { title: "Sort by type", bins: g.map(x => ({ key: x.key, label: x.label, emoji: x.emoji })), items: shuffle(g.flatMap(x => pick(x.items, 3).map(i => ({ ...i, key: `${x.key}-${i.key}`, bin: x.key })))) }; })(),
  { title: "Animals or Fruits?", bins: [{ key: "animals", label: "Animals", emoji: "🐾" }, { key: "fruits", label: "Fruits", emoji: "🍎" }],
    items: shuffle([...pick(ANIMALS, 3).map(a => ({ key: `a-${a.key}`, label: a.label, emoji: a.emoji, bin: "animals" })), ...pick(FRUITS, 3).map(f => ({ key: `f-${f.key}`, label: f.label, emoji: f.emoji, bin: "fruits" }))]) },
]);

/* ---------- Find the Correct Object game ---------- */
export const findObjectItems = () => {
  const pool = [...EVERYDAY_ITEMS, ...FRUITS, ...ANIMALS.map(a => ({ key: a.key, label: a.label, emoji: a.emoji }))];
  return pick(pool, 8).map(it => ({
    ask: `Find the ${it.label}`, voice: `Can you find the ${it.label}?`, correctKey: it.key,
    prompt: { type: "none" }, cols: 4, hideLabels: true, options: withCorrect({ key: it.key, label: it.label, emoji: it.emoji }, others(pool, it, 11).map(x => ({ key: x.key, label: x.label, emoji: x.emoji }))),
  }));
};

/* ---------- Memory decks ---------- */
export const MEMORY_DECKS = [
  { key: "animals", label: "Animals", emoji: "🐾", cards: ANIMALS.map(a => ({ v: a.emoji, say: a.label })) },
  { key: "fruits", label: "Fruits", emoji: "🍓", cards: FRUITS.map(f => ({ v: f.emoji, say: f.label })) },
  { key: "toys", label: "Toys", emoji: "🧸", cards: EVERYDAY[0].items.map(t => ({ v: t.emoji, say: t.label })) },
  { key: "letters", label: "Letters", emoji: "🔤", cards: LETTERS.map(l => ({ v: l.letter, say: l.letter, text: true })) },
  { key: "numbers", label: "Numbers", emoji: "🔢", cards: Array.from({ length: 10 }, (_, i) => ({ v: String(i + 1), say: String(i + 1), text: true })) },
];
